import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { P, useAuth } from './auth';

/**
 * A day worked more than an hour short of its hours (CR-004): an administrator
 * decides it — a whole percentage of one day's pay, or one day of annual leave
 * instead. Nothing is deducted while it waits.
 */
export type ShortfallStatus = 'PENDING_ADMIN_REVIEW' | 'RESOLVED_SALARY' | 'RESOLVED_LEAVE' | 'RESOLVED_EXCUSED';
/** EXCUSED (operator, 2026-10-28): nothing taken; the reason is required and stays the administrators'. */
export type ShortfallDecision = 'SALARY_PERCENTAGE' | 'LEAVE_ONE_DAY' | 'EXCUSED';
export type LeaveRouteProblem = 'LEAVE_TYPE_UNAVAILABLE' | 'INSUFFICIENT_BALANCE';

/** The most an administrator may deduct: 200 % of one day's pay. The server holds the same line. */
export const MAX_DEDUCTION_PERCENT = 200;

export interface PersonRef {
  id: string;
  fullName: string;
  fullNameAr: string | null;
  employeeCode?: string | null;
}

export interface ShortfallRow {
  id: string;
  user: PersonRef;
  workDate: string;
  requiredMinutes: number;
  workedMinutes: number;
  shortfallMinutes: number;
  hoursSource: 'SHIFT' | 'DEFAULT' | null;
  thresholdMinutes: number;
  status: ShortfallStatus;
  /** Changed by every decision and reversal: a decision names the one it saw. */
  version: number;
  decision: ShortfallDecision | null;
  deductionPercent: number | null;
  deductionDays: number | null;
  leaveDays: number;
  penaltyId: string | null;
  leaveGrantId: string | null;
  decidedAt: string | null;
  decidedBy: PersonRef | null;
  decisionNote: string | null;
  createdAt: string;
  can: { resolve: boolean; reverse: boolean };
}

export interface ShortfallAction {
  id: string;
  version: number;
  kind: 'RESOLVED' | 'REVERSED';
  decision: ShortfallDecision;
  deductionPercent: number | null;
  days: number | null;
  /** Only for whoever may see salaries. */
  amount?: number | null;
  currency?: string | null;
  dailyRate?: number | null;
  leaveDays: number | null;
  leaveYear: number | null;
  penaltyId: string | null;
  leaveGrantId: string | null;
  actor: PersonRef | null;
  note: string | null;
  createdAt: string;
}

export interface ShortfallDetail extends ShortfallRow {
  actions: ShortfallAction[];
  /** What a salary decision in force posted, and what became of it since. */
  penalty: {
    id: string;
    status: 'PENDING' | 'APPROVED' | 'APPLIED_TO_PAYROLL' | 'CANCELLED';
    days: number | null;
    amount?: number;
    currency?: string;
    effectiveMonth: string;
    onPayroll: boolean;
    cancelledAt: string | null;
    cancellationReason: string | null;
    walletPoints: number | null;
    objection: string | null;
  } | null;
  leaveGrant: {
    id: string;
    year: number;
    days: number;
    leaveType: { code: string; name: string; nameAr: string | null };
  } | null;
  options: {
    maxPercent: number;
    /** One day's pay on the work date — only for whoever may see salaries. */
    salary: { dailyRate: number | null; currency: string } | null;
    salaryVisible: boolean;
    leave: {
      year: number;
      leaveType: { code: string; name: string; nameAr: string | null } | null;
      remaining: number | null;
      pendingDays: number;
      deductedDays: number;
      problem: LeaveRouteProblem | null;
    };
    payroll: { effectiveMonth: string; periodId: string | null; status: string | null; signedOff: boolean };
  };
}

/** The signed-in employee's own short day — no administrator's note, nor who decided. */
export interface MyShortfall {
  id: string;
  workDate: string;
  requiredMinutes: number;
  workedMinutes: number;
  shortfallMinutes: number;
  status: ShortfallStatus;
  decision: ShortfallDecision | null;
  deductionPercent: number | null;
  deductionDays: number | null;
  leaveDays: number;
  penaltyId: string | null;
  decidedAt: string | null;
}

export const SHORTFALL_TONE: Record<ShortfallStatus, 'warning' | 'error' | 'info' | 'success'> = {
  PENDING_ADMIN_REVIEW: 'warning',
  RESOLVED_SALARY: 'error',
  RESOLVED_LEAVE: 'info',
  RESOLVED_EXCUSED: 'success',
};

/** The money of a percentage of one day's pay at `rate`, rounded to the cent — the server's own sum. */
export function percentAmount(percent: number, rate: number): number {
  const days = Math.round(percent) / 100;
  return Math.round((days * rate + Number.EPSILON) * 100) / 100;
}

/** An excuse's reason: 3 to 1,000 characters once trimmed — what the server takes. */
export function excuseReasonOk(value: string): boolean {
  const reason = value.trim();
  return reason.length >= 3 && reason.length <= 1000;
}

/** A whole percentage from 1 to the most allowed — what the server takes. */
export function percentOk(value: string, max = MAX_DEDUCTION_PERCENT): boolean {
  if (!/^\d+$/.test(value.trim())) return false;
  const n = Number(value);
  return n >= 1 && n <= max;
}

/** How many short days wait for a decision — the Violations tab's and the sidebar's count, for administrators. */
export function useWaitingShortDays(): number {
  const { can } = useAuth();
  const enabled = can(P.PENALTIES_MANAGE);
  const { data } = useQuery({
    queryKey: ['shortfalls', 'count'],
    queryFn: () => api.get<{ count: number }>('/attendance-shortfalls/count'),
    enabled,
    refetchInterval: 60_000,
  });
  return enabled ? (data?.count ?? 0) : 0;
}
